import { Injectable } from '@angular/core';
import { type ActivatedRouteSnapshot } from '@angular/router';
import { sortByKey, sortByUserLastName } from './util.functions';

export type SupportedPlatform = 'iOS' | 'Android';
type MobileOS = SupportedPlatform | 'WindowsPhone' | 'unknown';

export const IOS_APP_STORE_ID = '1460775077';

export const EMAIL_REGEX = /^(?:[^@\s();,:<>[\]\\<>]+)@(?:[^@\s_.+<>]+(\.[^@\s_.+<>]+)+)$/;

export function matchProperty(item: any, propertyKey: string, searchString: string) {
  if (!item || !propertyKey) {
    return false;
  }

  // try to return the requested value
  const propertyValue = propertyKey.split('.').reduce((prev, curr) => {
    return prev ? prev[curr] : undefined;
  }, item);

  if (!propertyValue) {
    return false;
  }

  const regex = new RegExp('.*' + searchString + '.*', 'i');

  return propertyValue.match(regex);
}

export function filterBySearchString<T>(
  array: T[],
  searchString: string,
  matchProperties: string[] = ['name', 'label'],
): T[] {
  if (!array) {
    return [];
  }

  if (searchString) {
    searchString = searchString.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

    // filter the array by string

    array = array.filter((arrayItem) => {
      for (const property of matchProperties) {
        if (matchProperty(arrayItem, property, searchString)) {
          return true;
        }
      }
      return false;
    });
  }

  return array;
}

export function matchPropertiesBySearchString(
  item: any,
  searchString: string,
  matchProperties: string[] = ['name', 'label'],
): boolean {
  if (!item) {
    return false;
  }

  if (searchString) {
    searchString = searchString.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

    for (const property of matchProperties) {
      if (matchProperty(item, property, searchString)) {
        return true;
      }
    }
  }

  return false;
}

export function getIdFromGid(gid: string): number {
  const decodedGid = window.atob(gid);
  const id = decodedGid.replace(/gid:\/\/reconcept-portfolio\/\w+\//, '');
  return Number(id);
}

@Injectable({
  providedIn: 'root',
})
export class UtilService {
  private readonly appStoreUrls: Record<SupportedPlatform, string> = {
    iOS: `https://apps.apple.com/nl/app/reconcept/id${IOS_APP_STORE_ID}`,
    Android: 'https://play.google.com/store/apps/details?id=nl.reconcept.portfolio.universal',
  };

  getParentPathFromRouteSnapshot(route: ActivatedRouteSnapshot) {
    let basePath = '';
    let parent = route.parent;

    while (parent) {
      if (parent.url[0]) {
        basePath = `${parent.url[0].path}/${basePath}`;
      }
      parent = parent.parent || null;
    }

    return basePath;
  }

  validateEmail(email: string) {
    return EMAIL_REGEX.test(email);
  }

  sortByKey(items: any[], key: string, inverse?: boolean) {
    return sortByKey(items, key, inverse);
  }

  sortByUserLastName<T extends { user: { lastName?: string } }>(items: T[]): T[] {
    return sortByUserLastName(items);
  }

  filterBySearchString<T>(array: T[], searchString: string, matchProperties: string[] = ['name', 'label']): T[] {
    return filterBySearchString(array, searchString, matchProperties);
  }

  // performs a Json stringify but ignoring circular references
  JSONStringifyCircular(obj: any): string {
    const getCircularReplacer = () => {
      const seen = new WeakSet();
      return (key: any, value: any) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return;
          }
          seen.add(value);
        }
        return value;
      };
    };

    return JSON.stringify(obj, getCircularReplacer());
  }

  animateScroll(element: any, start: any, end: any, duration: any) {
    const easeOutQuad = function (x: any, t: any, b: any, c: any, d: any) {
      return -c * (t /= d) * (t - 2) + b;
    };

    const delta = end - start;
    let startTime: any;

    // @ts-expect-error TS2774 function always defined
    if (window.performance && window.performance.now) {
      startTime = performance.now();
    } else if (Date.now) {
      startTime = Date.now();
    } else {
      startTime = new Date().getTime();
    }

    const tweenLoop = function (time?: any) {
      const t = !time ? 0 : time - startTime;
      const factor = easeOutQuad(null, t, 0, 1, duration);
      element.scrollLeft = start + delta * factor;
      if (t < duration && element.scrollLeft !== end) {
        requestAnimationFrame(tweenLoop);
      }
    };

    tweenLoop();
  }

  appIsAvailableForCurrentMobileOperatingSystem() {
    const os = this.determineMobileOperatingSystem();
    return os === 'iOS' || os === 'Android';
  }

  getAppDownloadLinkForMobileOperatingSystem() {
    const os = this.determineMobileOperatingSystem();
    const supportedOS = this.isSupportedOS(os);

    if (supportedOS) {
      return this.getAppDownloadLink(supportedOS);
    }

    return undefined;
  }

  determineMobileOperatingSystem(): MobileOS {
    const userAgent = navigator.userAgent || navigator.vendor;

    // Windows Phone detection - must come first because its UA also contains 'Android'
    if (/windows phone/i.test(userAgent)) {
      return 'WindowsPhone';
    }

    //Android detection
    if (/android/i.test(userAgent)) {
      return 'Android';
    }

    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if ((!!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)) || /iPad|iPhone|iPod/.test(userAgent)) {
      return 'iOS';
    }

    return 'unknown';
  }

  getAppDownloadLink(platform: SupportedPlatform): string {
    return this.appStoreUrls[platform];
  }

  isAndroid() {
    return this.determineMobileOperatingSystem() === 'Android';
  }

  private isSupportedOS(os: MobileOS): SupportedPlatform {
    if (os === 'iOS' || os === 'Android') {
      return os;
    }
    // @ts-expect-error TS2322 type not assignable
    return undefined;
  }
}
